import { useState } from 'react'
import { toast } from 'sonner'
import {
  BROADCAST_PRESET_META,
  BROADCAST_SEGMENT_KEYS,
  PASS_TYPES,
  type BroadcastSegmentKey,
  type PassType,
  type PlanCode,
  type ProgramCaps,
} from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { Modal } from '../components/Modal.js'
import { ProgramCapsFields } from '../components/ProgramCapsFields.js'
import { AdminApiError } from '../adminApi.js'
import { usePlanTiers } from '../hooks/useTenants.js'
import { useUpdatePlanTier } from '../hooks/usePlanTierEditor.js'
import { fmtMoney } from '../format.js'
import type { PlanTierRow } from '../types.js'

/**
 * Plan tier editor. Three rows (Starter / Pro / Business). Each row's price,
 * limits, and capabilities are editable; the tier identity is read-only.
 * Annual price is auto-derived as monthly × 11 (1 month free — the business
 * rule) so the admin only sets the monthly figure.
 */

// Human label for any broadcast segment key — the two legacy kinds plus the
// 8 curated presets (which carry shared meta).
function segmentLabel(key: BroadcastSegmentKey): string {
  if (key === 'everyone') return 'Everyone'
  if (key === 'birthday_this_month') return 'Birthday this month'
  return BROADCAST_PRESET_META[key].label
}

const PASS_TYPE_LABEL: Record<PassType, string> = {
  loyalty: 'Loyalty',
  membership: 'Membership / VIP',
  event_ticket: 'Event ticket',
}

interface DraftState {
  monthly_pesos: string
  max_branches: string
  max_staff_per_branch: string
  max_active_customers: string
  broadcasts_enabled: boolean
  segments: string[]
  filter_builder_enabled: boolean
  // New-tenant default broadcast cadence caps. Blank = unlimited.
  max_broadcasts_per_day: string
  max_broadcasts_per_week: string
  // SLICE-PASS-TYPES — per-type program caps (tier default).
  program_caps: ProgramCaps
}

function toDraft(row: PlanTierRow): DraftState {
  return {
    monthly_pesos: String(Math.round(row.monthly_price_centavos / 100)),
    max_branches: row.max_branches === null ? '' : String(row.max_branches),
    max_staff_per_branch:
      row.max_staff_per_branch === null
        ? ''
        : String(row.max_staff_per_branch),
    max_active_customers:
      row.max_active_customers === null
        ? ''
        : String(row.max_active_customers),
    broadcasts_enabled: row.broadcasts_enabled,
    segments: row.broadcast_segments_enabled,
    filter_builder_enabled: row.broadcast_filter_builder_enabled,
    max_broadcasts_per_day:
      row.max_broadcasts_per_day === null
        ? ''
        : String(row.max_broadcasts_per_day),
    max_broadcasts_per_week:
      row.max_broadcasts_per_week === null
        ? ''
        : String(row.max_broadcasts_per_week),
    program_caps: row.program_caps,
  }
}

/** Blank → null (unlimited); else a non-negative integer or NaN. Unlike
 *  parseLimit, 0 is a valid value here (0 broadcasts = none allowed). */
function parseCadence(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  return Number.parseInt(trimmed, 10)
}

/** Empty string → null (unlimited); otherwise a positive integer or NaN. */
function parseLimit(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  return Number.parseInt(trimmed, 10)
}

export function PlanTiers() {
  const tiers = usePlanTiers()
  const update = useUpdatePlanTier()

  // The tier currently open in the edit modal, plus its working draft.
  const [editing, setEditing] = useState<PlanCode | null>(null)
  const [draft, setDraft] = useState<DraftState | null>(null)

  function openEditor(row: PlanTierRow) {
    setEditing(row.tier)
    setDraft(toDraft(row))
  }

  function closeEditor() {
    setEditing(null)
    setDraft(null)
  }

  const editingRow = tiers.data?.plan_tiers.find((p) => p.tier === editing)

  function save() {
    if (!editing || !draft || !editingRow) return

    const monthlyPesos = Number.parseInt(draft.monthly_pesos, 10)
    if (!Number.isFinite(monthlyPesos) || monthlyPesos <= 0) {
      toast.error('Monthly price must be a positive number.')
      return
    }
    const monthly = monthlyPesos * 100
    // Annual = 11 months' worth (1 month free) — the locked business rule.
    const annual = monthly * 11

    const maxBranches = parseLimit(draft.max_branches)
    const maxStaff = parseLimit(draft.max_staff_per_branch)
    const maxCustomers = parseLimit(draft.max_active_customers)
    for (const v of [maxBranches, maxStaff, maxCustomers]) {
      if (v !== null && (!Number.isFinite(v) || v <= 0)) {
        toast.error('Limits must be blank (unlimited) or a positive number.')
        return
      }
    }
    const perDay = parseCadence(draft.max_broadcasts_per_day)
    const perWeek = parseCadence(draft.max_broadcasts_per_week)
    for (const v of [perDay, perWeek]) {
      if (v !== null && (!Number.isFinite(v) || v < 0)) {
        toast.error('Broadcast caps must be blank (unlimited) or 0 or more.')
        return
      }
    }
    update.mutate(
      {
        tier: editing,
        monthly_price_centavos: monthly,
        annual_price_centavos: annual,
        max_branches: maxBranches,
        max_staff_per_branch: maxStaff,
        max_active_customers: maxCustomers,
        broadcasts_enabled: draft.broadcasts_enabled,
        broadcast_segments_enabled: draft.broadcasts_enabled
          ? (draft.segments as BroadcastSegmentKey[])
          : [],
        broadcast_filter_builder_enabled: draft.broadcasts_enabled
          ? draft.filter_builder_enabled
          : false,
        max_broadcasts_per_day: perDay,
        max_broadcasts_per_week: perWeek,
        program_caps: draft.program_caps,
      },
      {
        onSuccess: () => {
          toast.success(`${editingRow.display_name} plan updated.`)
          closeEditor()
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not save the plan tier.',
          ),
      },
    )
  }

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <div>
          <h1>Plan tiers</h1>
          <p className="admin-audit-meta" style={{ marginTop: 6 }}>
            Price and capability changes apply at the next invoice generation.
          </p>
        </div>
      </div>

      {tiers.isLoading ? (
        <p className="admin-audit-meta">Loading plan tiers…</p>
      ) : tiers.isError ? (
        <div className="admin-error-state">
          <p>Couldn't load plan tiers.</p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void tiers.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {tiers.data!.plan_tiers.map((row) => (
            <div key={row.tier} className="admin-card">
              <div className="admin-page-head" style={{ marginBottom: 14 }}>
                <div>
                  <h3>{row.display_name}</h3>
                  <span className="admin-audit-meta">
                    {row.tier} · identity locked
                  </span>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-sm"
                  onClick={() => openEditor(row)}
                >
                  Edit
                </button>
              </div>
              <div className="admin-stat-grid">
                <div className="admin-stat">
                  <div className="admin-stat-label">Monthly</div>
                  <div className="admin-stat-value">
                    {fmtMoney(row.monthly_price_centavos)}
                  </div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat-label">Annual</div>
                  <div className="admin-stat-value">
                    {fmtMoney(row.annual_price_centavos)}
                  </div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat-label">Max branches</div>
                  <div className="admin-stat-value">
                    {row.max_branches ?? '∞'}
                  </div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat-label">Staff / branch</div>
                  <div className="admin-stat-value">
                    {row.max_staff_per_branch ?? '∞'}
                  </div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat-label">Active customers</div>
                  <div className="admin-stat-value">
                    {row.max_active_customers ?? '∞'}
                  </div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat-label">Broadcasts</div>
                  <div className="admin-stat-value">
                    {row.broadcasts_enabled ? 'On' : 'Off'}
                  </div>
                </div>
              </div>
              <p className="admin-audit-meta" style={{ marginTop: 10 }}>
                Programs:{' '}
                {PASS_TYPES.map((pt) => {
                  const c = row.program_caps[pt]
                  const v = c === null ? '∞' : c === 0 ? 'off' : String(c)
                  return `${PASS_TYPE_LABEL[pt]} ${v}`
                }).join(' · ')}
              </p>
              {row.broadcasts_enabled && (
                <p className="admin-audit-meta" style={{ marginTop: 10 }}>
                  Segments:{' '}
                  {row.broadcast_segments_enabled.length > 0
                    ? row.broadcast_segments_enabled
                        .map((s) => segmentLabel(s as BroadcastSegmentKey))
                        .join(', ')
                    : 'none'}
                  {' · '}
                  Filter builder:{' '}
                  {row.broadcast_filter_builder_enabled ? 'on' : 'off'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && draft && editingRow && (
        <Modal
          title={`Edit ${editingRow.display_name} plan`}
          onClose={closeEditor}
        >
          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-monthly">
              Monthly price (₱)
            </label>
            <input
              id="pt-monthly"
              className="admin-input"
              inputMode="numeric"
              value={draft.monthly_pesos}
              onChange={(e) =>
                setDraft({ ...draft, monthly_pesos: e.target.value })
              }
            />
            <p className="admin-hint">
              Annual is auto-set to 11× monthly (1 month free):{' '}
              {Number.isFinite(Number.parseInt(draft.monthly_pesos, 10))
                ? fmtMoney(
                    Number.parseInt(draft.monthly_pesos, 10) * 100 * 11,
                  )
                : '—'}
            </p>
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-branches">
              Max branches (blank = unlimited)
            </label>
            <input
              id="pt-branches"
              className="admin-input"
              inputMode="numeric"
              placeholder="Unlimited"
              value={draft.max_branches}
              onChange={(e) =>
                setDraft({ ...draft, max_branches: e.target.value })
              }
            />
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-staff">
              Max staff per branch (blank = unlimited)
            </label>
            <input
              id="pt-staff"
              className="admin-input"
              inputMode="numeric"
              placeholder="Unlimited"
              value={draft.max_staff_per_branch}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  max_staff_per_branch: e.target.value,
                })
              }
            />
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-customers">
              Max active customers (blank = unlimited)
            </label>
            <input
              id="pt-customers"
              className="admin-input"
              inputMode="numeric"
              placeholder="Unlimited"
              value={draft.max_active_customers}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  max_active_customers: e.target.value,
                })
              }
            />
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-bc-week">
              Broadcasts per week (blank = unlimited)
            </label>
            <input
              id="pt-bc-week"
              className="admin-input"
              inputMode="numeric"
              placeholder="Unlimited"
              value={draft.max_broadcasts_per_week}
              onChange={(e) =>
                setDraft({ ...draft, max_broadcasts_per_week: e.target.value })
              }
            />
            <p className="admin-hint">
              New-tenant default cadence cap. The tenant-create form pre-fills
              from this; admins can override per tenant.
            </p>
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pt-bc-day">
              Broadcasts per day (blank = unlimited)
            </label>
            <input
              id="pt-bc-day"
              className="admin-input"
              inputMode="numeric"
              placeholder="Unlimited"
              value={draft.max_broadcasts_per_day}
              onChange={(e) =>
                setDraft({ ...draft, max_broadcasts_per_day: e.target.value })
              }
            />
          </div>

          <div className="admin-field">
            <label className="admin-label">Programs per type</label>
            <p className="admin-hint" style={{ marginTop: 0, marginBottom: 6 }}>
              Default per-type program caps for tenants on this tier (per-tenant
              override available on the tenant).
            </p>
            <ProgramCapsFields
              value={draft.program_caps}
              onChange={(program_caps) => setDraft({ ...draft, program_caps })}
              idPrefix="pt-caps"
            />
          </div>

          <div className="admin-field">
            <label className="admin-label">Capabilities</label>
            <label className="admin-check-row">
              <input
                type="checkbox"
                checked={draft.broadcasts_enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    broadcasts_enabled: e.target.checked,
                  })
                }
              />
              Broadcasts enabled
            </label>
            {draft.broadcasts_enabled && (
              <>
                <p
                  className="admin-hint"
                  style={{ marginLeft: 22, marginTop: 6 }}
                >
                  Curated segments tenants on this tier can target:
                </p>
                {BROADCAST_SEGMENT_KEYS.map((seg) => (
                  <label
                    key={seg}
                    className="admin-check-row"
                    style={{ marginLeft: 22 }}
                  >
                    <input
                      type="checkbox"
                      checked={draft.segments.includes(seg)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          segments: e.target.checked
                            ? [...draft.segments, seg]
                            : draft.segments.filter((s) => s !== seg),
                        })
                      }
                    />
                    {segmentLabel(seg)}
                  </label>
                ))}
                <label
                  className="admin-check-row"
                  style={{ marginLeft: 22, marginTop: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={draft.filter_builder_enabled}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        filter_builder_enabled: e.target.checked,
                      })
                    }
                  />
                  Custom filter builder (power-user audience builder)
                </label>
              </>
            )}
          </div>

          <div className="admin-pending-note">
            Changes apply at next invoice generation for tenants on this tier.
          </div>

          <div className="admin-form-actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={save}
              disabled={update.isPending}
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              className="admin-btn"
              onClick={closeEditor}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  )
}
